import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import { LabelWithControl } from "components/common/LabelControl";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { SOMS_API_KEY } from "services/CONSTANTS";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { viewManualOrders } from "redux/actions/ViewManualOrdersAction";
import dayjs from "dayjs";
import { downloadManualOrders } from "redux/actions/downloadManualOrdersAction";
import {
  CLEAR_CSV_DATA,
  GET_MANUAL_ORDER_RESET,
  GET_MANUAL_ORDER_RESET_DOWNLOAD,
} from "redux/actions/action-type";
import { PAGE } from "components/common/CONSTANTS";

export const ViewManualOrders = () => {
  const { orders, metadata, isviewManualOrdersErrorLoading, errorDetails } =
    useSelector((state) => state.viewManualorders);
  const { csvData, isDownloadManualOrdersError } = useSelector(
    (state) => state.downloadManualordersReducer
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [orderDateFrom, setOrderDateFrom] = useState(new Date());
  const [orderDateTo, setOrderDateTo] = useState(new Date());
  const [packId, setPackId] = useState("");
  const [itemId, setItemId] = useState("");
  const [shipFromLocationId, setShipFromLocationId] = useState("");
  const [shipToLocationId, setShipToLocationId] = useState("");
  const [isSearched, setIsSearched] = useState(false);
  const [orderType, setOrderType] = useState("CENTRAL_SUPPLYCHAIN");
  const [changeOrderType, setChangeOrderType] = useState(true);
  const [status, setStatus] = useState("all");
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const userType = useSelector((state) => state.user);
  const { locationResponse } = useSelector((state) => state.locations);
  const { accessiblePages } = userType.userType;
  const [validStores, setValidStores] = useState([]);
  const FETCH_ORDER_LIMIT = 100;
  const [offset, setOffset] = useState(0);
  const tableContainerRef = useRef();
  const isMounted = useRef();

  const handleInfiniteScroll = useCallback(async () => {
    try {
      const { scrollTop, scrollHeight, clientHeight } =
        tableContainerRef.current;
      if (
        scrollTop + clientHeight >= scrollHeight - 1 &&
        !isviewManualOrdersErrorLoading
      ) {
        setOffset(orders.length);
      }
    } catch (error) {
      console.log(error, "error");
    }
  }, [tableContainerRef, orders, isviewManualOrdersErrorLoading]);

  useEffect(() => {
    if (orders.length > 0) {
      const tableContainer = tableContainerRef.current;
      tableContainer.addEventListener("scroll", handleInfiniteScroll);
      return () =>
        tableContainer.removeEventListener("scroll", handleInfiniteScroll);
    }
  }, [handleInfiniteScroll, orders]);

  useEffect(() => {
    if (metadata.next) {
      callSearchOrdersApi();
    }
  }, [offset]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (accessiblePages) {
      let channelList = accessiblePages[PAGE.VIEW_MANUAL_ORDERS.key];
      const stores = [];
      locationResponse.forEach(function (location) {
        if (channelList.indexOf(location.channel.toLowerCase()) > -1) {
          stores.push(location.locationId);
        }
      });
      setValidStores(stores);
    }
  }, [accessiblePages, locationResponse]);

  useEffect(() => {
    if (isDownloadManualOrdersError) {
      const errors = [];
      errors.push("No matching records found to download!");
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      } else {
        setValidationErrors([]); // Clear validation errors if all fields are valid
      }
    }
  }, [isDownloadManualOrdersError]);

  //Disabled eslint check. Adding missing dependency causes rerenders.
  useEffect(() => {
    if (isMounted.current) return;

    isMounted.current = true;
    dispatch({ type: GET_MANUAL_ORDER_RESET });
    dispatch({ type: GET_MANUAL_ORDER_RESET_DOWNLOAD });
    return () => {
      isMounted.current = false;
      dispatch({ type: GET_MANUAL_ORDER_RESET });
      dispatch({ type: GET_MANUAL_ORDER_RESET_DOWNLOAD });
    };
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  const isNumericWithCommas = (value) => {
    return value.split(",").every((v) => /^[0-9, \s]*$/.test(v.trim()));
  };

  const updateFieldErrors = (field, message) => {
    setFieldErrors((previous) => ({
      ...previous,
      [field]: {
        error: true,
        message: message,
      },
    }));
  };

  const handleSearch = () => {
    const updatedOffset = 0;
    setOffset(updatedOffset);
    dispatch({ type: GET_MANUAL_ORDER_RESET });
    callSearchOrdersApi(updatedOffset);
  };

  const callSearchOrdersApi = async (updatedOffset) => {
    const errors = [];
    const validSearchStores = [];
    let hasFieldErrors = false;
    // Validation to check if at least one ID is entered
    // if (!packId && !itemId) {
    //   errors.push("Please enter either MIN or PIN.");
    // }

    // Validation for both 'Order Date From' and 'Order Date To'
    if (!orderDateFrom || !orderDateTo) {
      errors.push(
        "Please enter both 'Order Date From' & 'Order Date To' dates"
      );
    }

    // Prepare arrays for all fields
    const packIdArray = packId
      ? packId.split(",").map((num) => Number(num.trim()))
      : [];
    const itemIdArray = itemId
      ? itemId.split(",").map((num) => Number(num.trim()))
      : [];
    const shipFromLocationIdArray = shipFromLocationId
      ? shipFromLocationId.split(",").map((num) => Number(num.trim()))
      : [];
    const shipToLocationIdArray = shipToLocationId
      ? shipToLocationId.split(",").map((num) => Number(num.trim()))
      : [];

    // Validation for array lengths
    setFieldErrors({});
    if (packId && !isNumericWithCommas(packId)) {
      updateFieldErrors("packId", "PIN should contain only numeric values.");
      hasFieldErrors = true;
    }
    if (packIdArray.length > 50) {
      updateFieldErrors("packId", "PIN can have a maximum of 50 entries.");
      hasFieldErrors = true;
    }
    if (itemId && !isNumericWithCommas(itemId)) {
      updateFieldErrors("itemId", "MIN should contain only numeric values.");
      hasFieldErrors = true;
    }
    if (itemIdArray.length > 50) {
      updateFieldErrors("itemId", "MIN can have a maximum of 50 entries.");
      hasFieldErrors = true;
    }
    if (shipFromLocationId && !isNumericWithCommas(shipFromLocationId)) {
      updateFieldErrors(
        "shipFromLocationId",
        "Depot should contain only numeric values."
      );
      hasFieldErrors = true;
    }
    if (shipFromLocationIdArray.length > 50) {
      updateFieldErrors(
        "shipFromLocationId",
        "Depot can have a maximum of 50 entries."
      );
      hasFieldErrors = true;
    }
    if (shipToLocationId && !isNumericWithCommas(shipToLocationId)) {
      updateFieldErrors(
        "shipToLocationId",
        "Store should contain only numeric values."
      );
      hasFieldErrors = true;
    }
    if (orderDateFrom && !dayjs(orderDateFrom).isValid()) {
      updateFieldErrors("orderDateFrom", "Please enter date in correct format");
      hasFieldErrors = true;
    }
    if (orderDateTo && !dayjs(orderDateTo).isValid()) {
      updateFieldErrors("orderDateTo", "Please enter date in correct format");
      hasFieldErrors = true;
    }

    // If there are validation errors, set them and return
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    } else {
      setValidationErrors([]);
      if (shipToLocationId) {
        const invalidStores = [];
        shipToLocationIdArray.forEach(function (store) {
          if (validStores.indexOf(store) < 0) {
            invalidStores.push(store);
          } else {
            validSearchStores.push(store);
          }
        });
        if (invalidStores.length > 0) {
          errors.push(
            `The following store(s) are not valid: ${invalidStores.join(", ")}`
          );
        }
      }
    }

    if (
      (!shipToLocationId || validSearchStores.length > 0) &&
      !hasFieldErrors
    ) {
      // Prepare the request body (reqBody) with all necessary data
      const reqBody = {
        orderDateFrom: orderDateFrom
          ? format(orderDateFrom, "yyyy-MM-dd")
          : null,
        orderDateTo: orderDateTo ? format(orderDateTo, "yyyy-MM-dd") : null,
        packId: packIdArray,
        itemId: itemIdArray,
        orderStatus: status === "all" ? "" : status,
        shipFromLocationId: shipFromLocationIdArray,
        shipToLocationId: shipToLocationId ? validSearchStores : validStores,
        userType: orderType === "all" ? "" : orderType,
      };

      try {
        // Dispatch the action to fetch data, passing the reqBody directly
        await dispatch(
          viewManualOrders({
            apiKey: SOMS_API_KEY, // Assuming apiKey is available
            limit: FETCH_ORDER_LIMIT, // Set default limit
            offset: updatedOffset !== undefined ? updatedOffset : offset, // Set default offset
            reqBody: reqBody, // Send the request body as the payload
          })
        );
      } catch (error) {
        // Handle unexpected errors
        // setValidationErrors(["An unexpected error occurred while fetching data."]);
        // console.error("Error in handleSearch:", error);
      }

      // Set the search state
      setIsSearched(true);
    }
  };

  const disablePastDate = (date) => {
    const day = dayjs().subtract(22, "day");
    return !dayjs(day).isBefore(date);
  };

  const handleOrderDateFromChange = (newDate) => {
    setOrderDateFrom(newDate);
    if (newDate) {
      const maxToDate = dayjs(newDate).add(4, "day").toDate();
      setOrderDateTo(maxToDate);
    }
  };
  const disableOrderDateTo = (date) => {
    const fromDate = dayjs(orderDateFrom);
    const toDate = fromDate.add(4, "day");
    const currentDate = dayjs(date);

    return (
      currentDate.isBefore(fromDate, "day") ||
      currentDate.isAfter(toDate, "day")
    );
  };

  const downloadCSV = async () => {
    const errors = [];
    const validSearchStores = [];

    if (!orderDateFrom || !orderDateTo) {
      errors.push(
        "Please enter both 'Order Date From' & 'Order Date To' dates"
      );
    }
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    const shipToLocationIdArray = shipToLocationId
      ? shipToLocationId.split(",").map((num) => Number(num.trim()))
      : [];
    if (shipToLocationId) {
      const invalidStores = [];
      shipToLocationIdArray.forEach(function (store) {
        if (validStores.indexOf(store) < 0) {
          invalidStores.push(store);
        } else {
          validSearchStores.push(store);
        }
      });
      if (invalidStores.length > 0) {
        errors.push(
          `The following store(s) are not valid: ${invalidStores.join(", ")}`
        );
      }
    }
    if (errors.length > 0) {
      setValidationErrors(errors);
    } else {
      setValidationErrors([]);
    }

    if (!shipToLocationId || validSearchStores.length > 0) {
      // Dispatch API call - data will update in Redux state
      dispatch(
        downloadManualOrders({
          apiKey: SOMS_API_KEY,
          limit: 15000,
          offset: 0,
          reqBody: {
            orderDateFrom: orderDateFrom
              ? format(orderDateFrom, "yyyy-MM-dd")
              : null,
            orderDateTo: orderDateTo ? format(orderDateTo, "yyyy-MM-dd") : null,
            packId: packId
              ? packId.split(",").map((num) => Number(num.trim()))
              : [],
            itemId: itemId
              ? itemId.split(",").map((num) => Number(num.trim()))
              : [],
            orderStatus: status === "all" ? "" : status,
            shipFromLocationId: shipFromLocationId
              ? shipFromLocationId.split(",").map((num) => Number(num.trim()))
              : [],
            shipToLocationId: shipToLocationId
              ? validSearchStores
              : validStores,
            userType: orderType === "all" ? "" : orderType,
          },
        })
      );
    }
  };

  const generateAndDownloadCSV = useCallback(
    (data) => {
      const csvRows = [];
      const headers = [
        "Order Date",
        "MIN",
        "PIN",
        "Item Description",
        "Pack Size",
        "Distribution Location ID(Depot)",
        "Destination Location ID(Store)",
        "Order ID",
        "Delivery Date",
        "Quantity Ordered in cases",
        "Order Status",
        "Apportioned Quantity in cases",
        "Fitted Quantity in cases",
        "Picked Quantity in cases",
        "Shipped Quantity in cases",
        "Difference-Ordered vs Apportioned",
        "Difference-Ordered vs Shipped",
      ];
      csvRows.push(headers.join(","));

      data.forEach((row) => {
        const values = [
          format(new Date(row.orderDate), "yyyyMMdd"),
          row.itemId,
          row.packId,
          row.itemDescription,
          row.packSize,
          row.shipFromLocationId,
          row.shipToLocationId,
          row.manualOrderId,
          format(new Date(row.deliveryDate), "yyyyMMdd"),
          row.quantityOrdered,
          toPascalCaseWithSpaces(row.status),
          row.shipmentDetail?.quantityAllocated ?? "", // Empty string if undefined
          row.shipmentDetail?.quantityFitted ?? "",
          row.shipmentDetail?.quantityPicked ?? "",
          row.shipmentDetail?.quantityShipped ?? "",
          row.quantityOrdered - (row.shipmentDetail?.quantityAllocated ?? ""), // Keep numeric calculation
          row.quantityOrdered - (row.shipmentDetail?.quantityShipped ?? ""),
        ];
        csvRows.push(values.join(","));
      });

      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });

      const uniqueCode = Array.from(crypto.getRandomValues(new Uint8Array(4))).map(b => b.toString(16).padStart(2, '0')).join('');
      let currentOrderType = orderType === "all" ? "All" : orderType;
      if (currentOrderType === "STORE_USER") {
        currentOrderType = "Store";
      } else if (currentOrderType === "CENTRAL_SUPPLYCHAIN") {
        currentOrderType = "Supply Chain";
      } else {
        currentOrderType =
          currentOrderType.charAt(0).toUpperCase() + currentOrderType.slice(1);
      }
      const currentDate = format(new Date(), "yyyy-MM-dd");
      const fileName = `ManualOrders_${currentOrderType}_${currentDate}_${uniqueCode}.csv`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [orderType]
  );
  // Trigger CSV download when csvData updates
  useEffect(() => {
    if (csvData && csvData.length > 0) {
      generateAndDownloadCSV(csvData);
      dispatch({ type: CLEAR_CSV_DATA });
    }
  }, [csvData, generateAndDownloadCSV, dispatch]); // Runs every time csvData changes

  const handleClose = () => navigate("/");

  const toPascalCaseWithSpaces = (str) => {
    if (!str) return "";
    return str
      .replace(/\s+/g, " ")
      .replace(/-/g, " ")
      .replace(/(?:^\w|\s\w)/g, (match) => match.toUpperCase());
  };

  return (
    <>
      <div className="mainPageStyling">
        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          View Manual Orders
        </Typography>
        <div className="labelControl">
          <Grid
            container
            spacing={2}
            style={{ marginBottom: "10px", fontSize: "15px" }}
          >
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Order Date From">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={orderDateFrom}
                    onChange={handleOrderDateFromChange}
                    shouldDisableDate={disablePastDate}
                    format="dd/MM/yyyy"
                    // disableFuture
                    slotProps={{
                      textField: {
                        inputProps: { "data-testid": "order-date-from-input" },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                  />
                </LocalizationProvider>
              </LabelWithControl>
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Order Date To">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={orderDateTo}
                    minDate={orderDateFrom}
                    format="dd/MM/yyyy"
                    shouldDisableDate={disableOrderDateTo}
                    onChange={(newDate) => setOrderDateTo(newDate)}
                    slotProps={{
                      textField: {
                        inputProps: { "data-testid": "order-date-to-input" },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                    // disableFuture
                  />
                </LocalizationProvider>
              </LabelWithControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3.5}>
              <LabelWithControl
                label={<>MIN &nbsp;or&nbsp; PIN</>}
              ></LabelWithControl>

              <TextField
                id="min"
                variant="outlined"
                size="small"
                placeholder="MIN"
                value={itemId}
                style={{ width: "47%" }}
                onChange={(e) => {
                  setItemId(e.target.value);
                  if (e.target.value) {
                    setPackId("");
                  }
                }}
                helperText={fieldErrors?.itemId?.message}
                error={fieldErrors?.itemId?.error}
                disabled={packId.length > 0}
                inputProps={{
                  style: {
                    background: packId.length > 0 ? "#f5f5f5" : "inherit",
                  },
                }}
              />
              <TextField
                id="pin"
                variant="outlined"
                size="small"
                placeholder="PIN"
                value={packId}
                style={{ width: "47%", marginLeft: "14px" }}
                onChange={(e) => {
                  setPackId(e.target.value);
                  if (e.target.value) {
                    setItemId("");
                  }
                }}
                helperText={fieldErrors?.packId?.message}
                error={fieldErrors?.packId?.error}
                disabled={itemId.length > 0}
                inputProps={{
                  style: {
                    background: itemId.length > 0 ? "#f5f5f5" : "inherit",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={1.25}>
              <LabelWithControl label="Depot">
                <TextField
                  placeholder="Depot"
                  value={shipFromLocationId}
                  onChange={(e) => setShipFromLocationId(e.target.value)}
                  helperText={fieldErrors?.shipFromLocationId?.message}
                  error={fieldErrors?.shipFromLocationId?.error}
                  fullWidth
                  size="small"
                />
              </LabelWithControl>
            </Grid>

            <Grid item xs={12} sm={6} md={1.25}>
              <LabelWithControl label="Store">
                <TextField
                  placeholder="Store"
                  value={shipToLocationId}
                  onChange={(e) => setShipToLocationId(e.target.value)}
                  helperText={fieldErrors?.shipToLocationId?.message}
                  error={fieldErrors?.shipToLocationId?.error}
                  fullWidth
                  size="small"
                />
              </LabelWithControl>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Select Order Type">
                <Select
                  className="dropDownValue"
                  value={orderType}
                  onChange={(e) => {
                    setOrderType(e.target.value);
                    setChangeOrderType(true);
                    setIsSearched(false);
                  }}
                  // label="Select Order Type"
                  fullWidth
                >
                  <MenuItem value="CENTRAL_SUPPLYCHAIN">Supply Chain</MenuItem>
                  <MenuItem value="STORE_USER">Store</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </LabelWithControl>
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <LabelWithControl label="Approval Status">
                <Select
                  className="dropDownValue"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    // setIsSearched(false)
                  }}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="auto-approved">Auto Approved</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                </Select>
              </LabelWithControl>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            style={{
              marginBottom: "10px",
              fontSize: "15px",
              justifyContent: "flex-end",
            }}
          >
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                size="large"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "2px 10px",
                  marginRight: "8px",
                  top: "0",
                }}
              >
                Search
              </Button>

              <Button
                href={orders.length > 0 ? "#" : "#"}
                onClick={downloadCSV}
                variant="contained"
                size="large"
                color="primary"
                style={{
                  fontWeight: "bold",
                  fontSize: "12px",
                  padding: "2px 10px",
                }}
              >
                Download
              </Button>
            </Grid>
          </Grid>
        </div>

        <Grid item>
          {validationErrors.length > 0 && (
            <div className="error-display">
              {validationErrors.map((error, idx) => (
                <p key={idx} style={{ color: "red" }}>
                  {error}
                </p>
              ))}
            </div>
          )}
        </Grid>
      </div>

      {/* Conditionally render table and related components */}
      {isSearched && (
        <>
          <TableContainer
            component={Paper}
            ref={tableContainerRef}
            className="tableContainerScroll tableStructure tableStyle"
          >
            <Table sx={{ borderLeft: "1px solid rgba(224, 224, 224, 1)" }}>
              {orders.length <= 600 && (
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Order Date
                    </TableCell>
                    {/* <TableCell>Manual Order ID</TableCell> */}
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      MIN
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      PIN
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Pack Size
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Depot
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Store
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Delivery Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Quantity Ordered
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Order Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Apportioned <br />
                      Quantity
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Fitted <br />
                      Quantity
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Picked <br />
                      Quantity
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "white",
                        padding: "3px",
                        borderRight: "1px solid rgba(224, 224, 224, 1)",
                        textAlign: "center",
                      }}
                    >
                      Shipped <br />
                      Quantity
                    </TableCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} style={{ textAlign: "center" }}>
                      {errorDetails && <>{errorDetails.errorMessage}</>}
                      {!errorDetails && (
                        <>
                          No matching records found! Please check your search
                          criteria.
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ) : orders.length > 600 ? (
                  <TableRow>
                    <TableCell
                      colSpan={14}
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "black",
                        marginBottom: "10px",
                        textAlign: "center",
                      }}
                    >
                      Large volume of data found. Use download button to
                      download manual orders.
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => (
                    <TableRow key={order.manualOrderId}>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {new Date(order.orderDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {order.itemId}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {order.packId}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "flex-start",
                        }}
                      >
                        <Tooltip title={order.itemDescription} arrow>
                          <span
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "inline-block",
                              maxWidth: "480px",
                              verticalAlign: "middle",
                            }}
                          >
                            {order.itemDescription}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {order.packSize}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {order.shipFromLocationId}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {order.shipToLocationId}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {order.deliveryDate
                          ? format(new Date(order.deliveryDate), "dd/MM/yyyy")
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {order.quantityOrdered}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {toPascalCaseWithSpaces(order.status)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {/* {order.shipmentDetail.quantityAllocated} */}
                        {order.shipmentDetail
                          ? order.shipmentDetail.quantityAllocated
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {/* {order.shipmentDetail.quantityFitted} */}
                        {order.shipmentDetail
                          ? order.shipmentDetail.quantityFitted
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {/* {order.shipmentDetail.quantityPicked} */}
                        {order.shipmentDetail
                          ? order.shipmentDetail.quantityPicked
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          padding: "3px",
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                          textAlign: "center",
                        }}
                      >
                        {/* {order.shipmentDetail.quantityShipped} */}
                        {order.shipmentDetail
                          ? order.shipmentDetail.quantityShipped
                          : ""}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {isviewManualOrdersErrorLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={14}
                      style={{
                        textAlign: "center",
                        position: "relative",
                        height: "100px",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 12,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <CircularProgress />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              marginTop: 8,
            }}
          ></div>

          <Grid
            container
            spacing={2}
            style={{ marginTop: "10px", marginBottom: "10px" }}
            className="paddingTop0 commonButtons marginBottom0"
          >
            <Grid item xs={12} sm={12} md={12} className="innerPadding">
              <Grid container justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={handleClose}
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    padding: "2px 10px",
                  }}
                >
                  Close
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      {/* Show "Enter your search criteria" when no search has been performed */}

      {(!isSearched || !changeOrderType) && (
        <TableContainer className="tableStructure tableStyle">
          <Table>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={16}
                  style={{ textAlign: "center", borderRight: "0" }}
                >
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      color: "black",
                      marginBottom: "10px",
                    }}
                  >
                    Please enter your search criteria
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(!isSearched || !changeOrderType) && (
        <Grid
          container
          justifyContent="flex-end"
          style={{ marginBottom: "10px", marginTop: "10px" }}
          className="paddingTop0 commonButtons marginBottom0"
        >
          <Grid item xs={12} sm={6} md={12} className="innerPadding">
            <Grid container justifyContent="flex-end">
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleClose}
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "2px 10px",
                }}
              >
                Close
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};
